import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { TableRow } from '@mui/material';
import TextTableCell from 'components/common/table/TextTableCell';
import useHistoryWithContext from 'components/menu/useHistoryWithContext';
import CheckboxTableCell from 'components/common/table/CheckboxTableCell';
import IconButton from 'components/common/buttons/IconButton';
import { usePermission } from 'providers/RightsHooks';
import { ADMIN_LEASING_INCOME_SCHEMAS_WRITE } from 'constants/rightsCodes';
import ActionsTableCell from 'components/common/table/ActionsTableCell';
import { stateHistoryLeasingIncomeSchemaId } from '../store';
import { useMutation } from 'hooks/apollo/useMutation';
import useConfirmDialog from 'features/common/components/confirm/useConfirmDialog';
import { useTranslation } from 'react-i18next';
import ListActionsMenu from 'features/common/components/list-actions-menu/ListActionsMenu';
import { DELETE_LEASING_INCOME_SCHEMA } from 'features/leasingIncomeSchema/mutations';

const ItemComponent = ({ data }) => {
  const { t } = useTranslation();
  const historyContext = useHistoryWithContext();
  const confirm = useConfirmDialog();

  const canWrite = usePermission(ADMIN_LEASING_INCOME_SCHEMAS_WRITE);

  const [pending, setPending] = useState(false);

  const handleEdit = useCallback(() => {
    historyContext.push(`/leasingIncomeSchemas/${data.id}`);
  }, [data, historyContext]);

  // #region Delete

  const [deleteLeasingIncomeSchema] = useMutation(DELETE_LEASING_INCOME_SCHEMA, {
    onError: _ => {
      setPending(false);
    }
  });

  const handleDelete = useCallback(() => {
    confirm(
      () => {
        setPending(true);
        deleteLeasingIncomeSchema({
          variables: { id: data.id }
        });
      },
      {
        content: t('LeasingIncomeSchema.ConfirmDelete', { name: data.name })
      }
    );
  }, [confirm, deleteLeasingIncomeSchema, t, data]);

  // #endregion

  const openStateHistory = useCallback(() => {
    stateHistoryLeasingIncomeSchemaId.set(data.id);
  }, [data]);

  const actions = useMemo(
    () => [
      { caption: 'Delete', handler: handleDelete, icon: 'delete', color: 'error', disabled: !canWrite || !data.isDeletable },
      { caption: 'LeasingIncomeSchema.ViewStateHistory', handler: openStateHistory, icon: 'history', color: 'warning' }
    ],
    [canWrite, data.isDeletable, handleDelete, openStateHistory]
  );

  return (
    <TableRow>
      <TextTableCell content={data.code} />
      <TextTableCell content={data.name} />
      <CheckboxTableCell checked={data.isActive} />
      <TextTableCell content={data.stateName} />
      <ActionsTableCell>
        <IconButton type={canWrite ? 'edit' : 'view'} onClick={handleEdit} disabled={pending} />
        <ListActionsMenu actions={actions} disabled={pending} />
      </ActionsTableCell>
    </TableRow>
  );
};

ItemComponent.propTypes = {
  data: PropTypes.object
};

export default ItemComponent;
